import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {RefCallback, useCallback, useEffect, useRef, useState} from 'react';

import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

import ProcedureValidationErrorInfoButton from './buttons/ProcedureValidationErrorInfoButton';
import {
  makePlaceholderNameClass,
  NODE_NAME_BOX_PADDING,
  nodeNameTextEllipsesClass,
} from './procedureMethodNodeCommonConsts';
import {ProcedureValidationObjectType} from './validation/ProcedureValidationTypes';

function useMethodNameOverflowCheck(name: string): {
  ref: RefCallback<HTMLDivElement>;
  isOverflow: boolean;
} {
  const [isOverflow, setIsOverflow] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const checkOverflow = useCallback((): void => {
    const element = elementRef.current;
    if (element == null) {
      return;
    }
    setIsOverflow(element.scrollHeight > element.offsetHeight + NODE_NAME_BOX_PADDING);
  }, []);

  const {ref: resizeRef} = useResizeObserver<HTMLDivElement>({onResize: checkOverflow});

  const ref = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      resizeRef(node);
      elementRef.current = node;
    },
    [resizeRef]
  );

  useEffect(() => {
    checkOverflow();
  }, [name, checkOverflow]);

  return {ref, isOverflow};
}

export function MethodNameViewer(props: {
  methodCid: string;
  name: string;
  isHoverable: boolean;
}): JSX.Element {
  const [onHover, hoverProps] = useHoverState();
  const {ref: methodNameRef, isOverflow: isMethodNameOverflow} = useMethodNameOverflowCheck(props.name);
  const methodNameContent = (
    <div {...getTestProps({component: 'MethodNameViewer', element: 'root'})}>{props.name}</div>
  );

  return (
    <div id={`${props.methodCid}-viewer`} className={classNames('nodrag', css(styles.nameAndValidationBox))}>
      <ProcedureValidationErrorInfoButton
        primaryObject={{id: props.methodCid, objectType: ProcedureValidationObjectType.METHOD}}
      />
      <div
        ref={methodNameRef}
        className={classNames(
          nodeNameTextEllipsesClass,
          makePlaceholderNameClass(props.name),
          css(props.isHoverable && onHover && styles.onHoverName)
        )}
        {...hoverProps}
      >
        <Tooltip
          placement={FloatingPlacement.TOP}
          text={methodNameContent}
          isDisabled={!isMethodNameOverflow}
        >
          {methodNameContent}
        </Tooltip>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  nameAndValidationBox: {
    display: 'flex',
    alignItems: 'center',
    ...multiLineEllipsisText(2),
  },
  onHoverName: {
    backgroundColor: GRAY,
    cursor: 'text',
    paddingRight: '8px',
    marginRight: '8px',
  },
});
